import _ from 'lodash';
import { useMutation } from "@apollo/client";

// Data
import {
  ADD_PLAYER_TO_ASSESSMENT,
  CREATE_PLAYER_ASSESSMENT,
  CREATE_PLAYER_ASSESSMENTS,
  PLAYER_ASSESSMENT_FIELDS,
  REMOVE_PLAYER_ASSESSMENTS,
  UPDATE_PLAYER_ASSESSMENT
} from "../queries/player-assessment.queries";
import { createCacheModifier, removeCacheModifiers } from "../cache/basic.cache";
import { ICreatePlayerProps } from '../../components/Player/CreatePlayer/create-player.interface';
import { getAuthUser } from './auth.service';

// The server lists an age group only while it holds players or sessions, so a roster change can add
// or remove one — every surface that lists age groups refetches, each under its own operation name.
const AGE_GROUP_POPULATION_REFETCH_OPERATIONS = ['GetAgeGroups', 'GetGlobalAgeGroupFilter', 'GetManageAgeGroups'];

interface IAddPlayerToAssessmentProps {
  playerId: string;
  ageGroupId: string;
  positionId: string;
}

export const buildAddPlayerToAssessment = () => {
  const user = getAuthUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(ADD_PLAYER_TO_ASSESSMENT);

  return {
    ...rest,
    addPlayerToAssessment: (data: IAddPlayerToAssessmentProps) => {
      return mutation({
        variables: { data: { ...data, assessmentId: user?.settings.activeAssessmentId } },
        refetchQueries: AGE_GROUP_POPULATION_REFETCH_OPERATIONS,
        update: (cache, { data }) => {
          if (data) {
            const playerAssessment = data.addPlayerToAssessment;

            cache.modify({
              fields: {
                playerAssessments: createCacheModifier({
                  cache,
                  createdDoc: playerAssessment,
                  fragment: PLAYER_ASSESSMENT_FIELDS,
                  fragmentName: 'PlayerAssessmentFields',
                  modelName: 'PlayerAssessment'
                })
              }
            })
          }
        }
      })
    }
  }
};

export const buildRemovePlayerAssessments = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(REMOVE_PLAYER_ASSESSMENTS);

  return {
    ...rest,
    removePlayerAssessments: (ids: string[]) => mutation({
      variables: { ids },
      refetchQueries: AGE_GROUP_POPULATION_REFETCH_OPERATIONS,
      update: cache => {
        cache.modify({
          fields: {
            playerAssessments: removeCacheModifiers(cache, ids)
          }
        })
      },
      optimisticResponse: {
        __typename: 'Mutation',
        removePlayerAssessments: ids.map(_id => ({
          __typename: 'DeleteResponse',
          _id
        }))
      }
    })
  };
};

export const buildCreatePlayerAssessment = () => {
  const user = getAuthUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(CREATE_PLAYER_ASSESSMENT);

  return {
    ...rest,
    createPlayerAssessment: (data: ICreatePlayerProps) => mutation({
      variables: {data, companyId: user?.settings.activeCompanyId},
      refetchQueries: AGE_GROUP_POPULATION_REFETCH_OPERATIONS,
      update: (cache, {data}) => {
        if (data) {
          cache.modify({
            fields: {
              playerAssessments: createCacheModifier({
                cache,
                createdDoc: data.createPlayerAssessment,
                fragment: PLAYER_ASSESSMENT_FIELDS,
                fragmentName: 'PlayerAssessmentFields',
                modelName: 'PlayerAssessment'
              })
            }
          })
        }
      },
      optimisticResponse: {
        __typename: 'Mutation',
        createPlayerAssessment: {
          __typename: 'PlayerAssessment',
          ageGroup: {
            _id: data.ageGroupId
          },
          position: {
            _id: data.positionId
          },
          player: {
            ..._.omit(data, ['positionId', 'ageGroupId'])
          }
        }
      }
    })
  };
};


export const buildCreatePlayerAssessments = () => {
  const user = getAuthUser();
  // 'all', not the default 'none': a partly-failed import still wrote rows, and 'none' skips the
  // update AND every refetch below, leaving the page stale about writes that really happened.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(CREATE_PLAYER_ASSESSMENTS, { errorPolicy: 'all' });

  return {
    ...rest,
    createPlayerAssessments: (rows: any[], fillMissingDateOfBirth?: DateOfBirthFallback, overwriteDateOfBirth?: boolean, retainJerseyAssignments?: boolean) => {
      return mutation({
        variables: { rows, companyId: user?.settings.activeCompanyId, fillMissingDateOfBirth, overwriteDateOfBirth, retainJerseyAssignments },
        // Aggregates the mutation can't return (the list's per-row counts, each age group's
        // numPlayers/numGoalies, the unsent-welcome count) are reconciled by refetching their queries.
        refetchQueries: ['GetPlayerList', ...AGE_GROUP_POPULATION_REFETCH_OPERATIONS, 'CountHasUnsentType'],
        // Settle every refetch before resolving, so the snackbar, the progress notice and all four
        // lists update in one paint instead of a visible cascade as each response lands.
        awaitRefetchQueries: true,
        // Null-tolerant throughout — a partial response can null the field or any row, and a throw
        // here is caught as a mutation error, which is what would skip the refetches again.
        update: (cache, { data }) => {
          const createdPlayerAssessment = (data?.createPlayerAssessments || [])
            .map((row: ICreatePlayerAssessmentRowResult) => row?.playerAssessment)
            .filter(Boolean);

          if (createdPlayerAssessment.length) {
            cache.modify({
              fields: {
                playerAssessments: createCacheModifier({
                  cache,
                  createdDoc: createdPlayerAssessment,
                  fragment: PLAYER_ASSESSMENT_FIELDS,
                  fragmentName: 'PlayerAssessmentFields',
                  modelName: 'PlayerAssessment'
                })
              }
            })
          }
        }
        // Deliberately not optimistic — see "A bulk import has no optimistic result" in CLAUDE.md.
      })
    }
  }
};

export const buildUpdatePlayerAssessment = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(UPDATE_PLAYER_ASSESSMENT, {
    context: {
      serializationKey: 'MUTATION',
      tracked: true,
    },
  });

  return {
    ...rest,
    updatePlayerAssessment: (_id: string, data: { positionId?: string }) =>
      mutation({
        variables: { _id, data }
      })
  };
};
